/**
 * ADS-49 — detect US sale / targeted-advertising opt-out so we can set `ooos=y`.
 *
 * Atlas published `ooos=y` when GPP said SaleOptOut or TargetedAdvertisingOptOut
 * was 1. Concert reads the same fact from the IAB `usprivacy` string (third
 * character `Y`) and/or parsed GPP sections OneTrust exposes on `__gpp`.
 * OneTrust cookie groups are not a source — `cookie_consent` stays separate.
 */

export const OOOS_KEY = 'ooos';
export const OOOS_VALUE = 'y';

/**
 * CCPA `usprivacy` is four characters: version, notice, sale-opt-out, LSPA.
 * The third character is `Y` when the user opted out of sale (`1YYN` / `1YYY`).
 *
 * @param {string} [usp]
 * @returns {boolean}
 */
export function usPrivacyOptedOutOfSale(usp) {
  return typeof usp === 'string' && usp.length >= 3 && usp.charAt(2) === 'Y';
}

/**
 * @param {Object} [section]
 * @returns {boolean}
 */
export function gppSectionOptedOut(section) {
  if (!section || typeof section !== 'object') return false;
  return section.SaleOptOut === 1 || section.TargetedAdvertisingOptOut === 1;
}

/**
 * @param {Record<string, Object|Object[]>} [parsedSections]
 * @returns {boolean}
 */
export function gppParsedSectionsOptedOut(parsedSections) {
  if (!parsedSections || typeof parsedSections !== 'object') return false;

  return Object.values(parsedSections).some(section => {
    const first = Array.isArray(section) ? section[0] : section;
    return gppSectionOptedOut(first);
  });
}

/**
 * @param {{ usPrivacy?: string, gppParsedSections?: Record<string, Object|Object[]> }} [signals]
 * @returns {boolean}
 */
export function isSaleOrTargetedAdvertisingOptedOut(signals = {}) {
  return usPrivacyOptedOutOfSale(signals.usPrivacy) || gppParsedSectionsOptedOut(signals.gppParsedSections);
}
